import { useEffect, useSyncExternalStore } from "react";
import { realtime, type RealtimeStatus } from "../api/ws";
import { useSiteStore } from "../stores/siteStore";

/**
 * App-wide realtime lifecycle. The socket used to live inside LivePage, so
 * every other page (/fleet, /alerts, Network counts) ran storeless and showed
 * everything off-line unless /live was open. One socket for the whole app,
 * reconnected when the active site changes (connect() captures the site id;
 * warehouse changes are followed internally by the client).
 */
export function useRealtime() {
  const activeSiteId = useSiteStore((s) => s.activeSiteId);
  const sitesResolved = useSiteStore((s) => s.sitesResolved);

  // Connect only once we know which site we are entitled to (ADR 225).
  //
  // Connecting earlier means subscribing with the FALLBACK site id, which the
  // realtime service answers by closing the session for a tenant violation —
  // and the client reconnects, forever. That storm is what made the badge flap
  // between hidden / DESCONECTADO / CONECTANDO for a grant-less user, and it
  // also cost one reconnect on every normal boot (the visible "blink") when the
  // stored site turned out not to be the resolved one.
  //
  // An empty activeSiteId after resolution is the grant-less case (ADR 167):
  // there is no channel to subscribe to, so we never open a socket at all.
  // Resolution FAILING leaves the fallback id in place on purpose — then we do
  // try, and the badge can report the outage instead of hiding it.
  const shouldConnect = sitesResolved && activeSiteId !== "";

  useEffect(() => {
    if (!shouldConnect) return;
    realtime.connect();
    return () => realtime.disconnect();
  }, [activeSiteId, shouldConnect]);
}

/**
 * Live transport state for the shell badge (ADR 224). The socket is an external
 * mutable source, so this is useSyncExternalStore rather than a store write from
 * inside an event handler — the client stays free of React imports, and the
 * snapshot is a plain string, so no tearing and no re-render on equal values.
 */
export function useRealtimeStatus(): RealtimeStatus {
  return useSyncExternalStore(realtime.subscribe, realtime.getStatus, realtime.getStatus);
}
